import React, { useState, Fragment, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  IconButton,
  Fade,
  Zoom,
  useMediaQuery,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Avatar,
  Modal,
  Backdrop,
  Paper,
  Divider,
  Grid2 as Grid,
} from "@mui/material";
import {
  AccountTree as HierarchyIcon,
  ViewModule as GridIcon,
  Business as BusinessIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Badge as BadgeIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";

const NotaryOrganization = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const currentUser = useSelector((state) => state.notarySession.user);
  const [viewMode, setViewMode] = useState("hierarchy");
  const [selectedNotary, setSelectedNotary] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);

  // Redirect to finalize page if user has no availabilities set
  useEffect(() => {
    if (currentUser?.availabilities.length === 0) {
      navigate("/notary/finalize");
      return;
    }
  }, [currentUser, navigate]);

  // Extract organization data
  const organizationData = currentUser?.my_all_notaries || {};
  const rawOrganizationName = organizationData.organization || "Organization";
  const organizationName =
    rawOrganizationName === "permitRockstar"
      ? "Permit Rockstar Notaries Team"
      : rawOrganizationName;
  const mainNotaries = organizationData.main_notaries || [];
  const subNotaries = organizationData.sub_notaries || [];
  const allNotaries = [...mainNotaries, ...subNotaries];

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
      setAnimationKey((prev) => prev + 1);
    }
  };

  const handleNotaryClick = (notary) => {
    setSelectedNotary(notary);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedNotary(null);
  };

  const isCurrentUser = (notary) => {
    return notary.id === currentUser?.id || notary.email === currentUser?.email;
  };

  const getNotaryStatusColor = (notary) => {
    if (notary.is_deactivated) return theme.palette.error.main;
    if (notary.on_site_registered && notary.bn_registered)
      return theme.palette.success.main;
    if (notary.on_site_registered || notary.bn_registered)
      return theme.palette.warning.main;
    return theme.palette.grey[500];
  };

  const formatAvailability = (availabilities) => {
    if (!availabilities || availabilities.length === 0)
      return "No availability set";

    const available = availabilities.filter(
      (av) => av.available || (av.start && av.end)
    );
    if (available.length === 0) return "No availability set";

    return available
      .map((av) => {
        const day = av.day;
        const start = av.startTime || av.start;
        const end = av.endTime || av.end;
        return `${day}: ${start}-${end}`;
      })
      .join(", ");
  };

  const formatNotaryName = (notary) => {
    // Use full_name if available, otherwise combine first_name and last_name
    let displayName =
      notary.full_name ||
      `${notary.first_name || ""} ${notary.last_name || ""}`.trim();

    // If still empty, use fallback
    if (!displayName) {
      return "Unknown";
    }

    // Remove "notary" from the name if present
    displayName = displayName.replace(/notary/gi, "").trim();

    // Convert to title case
    return displayName
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Hierarchical View Component
  const HierarchicalView = () => {
    const nodeSize = isMobile ? 80 : isTablet ? 100 : 120;
    const spacing = isMobile ? 15 : isTablet ? 20 : 25;

    return (
      <Fade in={true} timeout={800} key={`hierarchy-${animationKey}`}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 4,
            position: "relative",
          }}
        >
          {/* Organization Title */}
          <Box
            sx={{
              mb: 4,
              textAlign: "center",
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "100%",
                height: "100%",
                background: `linear-gradient(135deg, ${theme.palette.primary.light}20, ${theme.palette.secondary.light}20)`,
                borderRadius: "20px",
                zIndex: -1,
              },
            }}
          >
            <Typography
              variant={isMobile ? "h5" : "h3"}
              component="h2"
              sx={{
                fontWeight: "bold",
                color: theme.palette.primary.main,
                textAlign: "center",
                py: 2,
                px: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                textShadow: "none",
              }}
            >
              <BusinessIcon
                sx={{
                  mr: 1,
                  verticalAlign: "middle",
                  color: theme.palette.primary.main,
                  filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
                }}
              />
              {organizationName}
            </Typography>
          </Box>

          {/* Main Notaries Level */}
          {mainNotaries.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  textAlign: "center",
                  mb: 2,
                  color: theme.palette.text.secondary,
                  fontWeight: "medium",
                }}
              >
                Main Notaries
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "nowrap",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 0,
                }}
              >
                {mainNotaries.map((notary, index) => (
                  <Fragment key={notary.id}>
                    <Zoom in={true} timeout={600 + index * 200}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            cursor: "pointer",
                            transition: "transform 0.3s ease",
                            position: "relative",
                            "&:hover": {
                              transform: "scale(1.05)",
                            },
                          }}
                          onClick={() => handleNotaryClick(notary)}
                        >
                          <Avatar
                            sx={{
                              width: nodeSize,
                              height: nodeSize,
                              border: isCurrentUser(notary)
                                ? `4px solid ${theme.palette.primary.main}`
                                : `3px solid ${getNotaryStatusColor(notary)}`,
                              boxShadow: `0 8px 32px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08)`,
                              fontSize: isMobile ? "1.5rem" : "2rem",
                              fontWeight: "bold",
                              bgcolor: theme.palette.background.paper,
                              color: theme.palette.text.primary,
                              position: "relative",
                              overflow: "visible",
                              background: notary.avatar
                                ? undefined
                                : `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.secondary.light})`,
                              transition:
                                "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                              "&:hover": {
                                transform: "translateY(-4px)",
                                boxShadow: `0 12px 40px rgba(0,0,0,0.15), 0 6px 20px rgba(0,0,0,0.1)`,
                              },
                            }}
                            src={notary.avatar}
                          >
                            {(
                              notary.full_name?.charAt(0) ||
                              notary.first_name?.charAt(0) ||
                              "N"
                            ).toUpperCase()}
                            {isCurrentUser(notary) && (
                              <Chip
                                label="YOU"
                                size="small"
                                sx={{
                                  position: "absolute",
                                  bottom: -12,
                                  left: "50%",
                                  transform: "translateX(-50%)",
                                  fontSize: "0.6rem",
                                  height: 18,
                                  bgcolor: theme.palette.primary.main,
                                  color: "white",
                                  fontWeight: "bold",
                                  zIndex: 1,
                                }}
                              />
                            )}
                          </Avatar>
                          <Typography
                            variant="caption"
                            sx={{
                              mt: 1,
                              textAlign: "center",
                              fontWeight: "medium",
                              maxWidth: nodeSize + 20,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatNotaryName(notary)}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.5,
                              mt: 0.5,
                              flexWrap: "wrap",
                              justifyContent: "center",
                            }}
                          >
                            {notary.on_site_registered && (
                              <Chip
                                label="On-Site"
                                size="small"
                                sx={{
                                  fontSize: "0.6rem",
                                  height: 16,
                                  bgcolor: theme.palette.success.light,
                                  color: theme.palette.success.contrastText,
                                }}
                              />
                            )}
                            {notary.bn_registered && (
                              <Chip
                                label="BN"
                                size="small"
                                sx={{
                                  fontSize: "0.6rem",
                                  height: 16,
                                  bgcolor: theme.palette.info.main,
                                  color: "white",
                                  fontWeight: "bold",
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                        {/* Connection line to next notary */}
                        {index < mainNotaries.length - 1 && (
                          <Box
                            sx={{
                              width: isMobile ? "50px" : "70px",
                              height: "3px",
                              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                              borderRadius: "2px",
                              boxShadow: `0 0 6px ${theme.palette.primary.main}40`,
                              marginTop: "-25px",
                              mx: 2,
                            }}
                          />
                        )}
                      </Box>
                    </Zoom>
                  </Fragment>
                ))}
              </Box>
            </Box>
          )}

          {/* Connection Lines */}
          {mainNotaries.length > 0 && subNotaries.length > 0 && (
            <Box
              sx={{
                width: "3px",
                height: spacing,
                background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                borderRadius: "2px",
                position: "relative",
                boxShadow: `0 0 10px ${theme.palette.primary.main}40`,
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: isMobile ? "120px" : "180px",
                  height: "3px",
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  borderRadius: "2px",
                  boxShadow: `0 0 8px ${theme.palette.primary.main}30`,
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "10px",
                  height: "10px",
                  background: theme.palette.primary.main,
                  borderRadius: "50%",
                  boxShadow: `0 0 12px ${theme.palette.primary.main}60, inset 0 0 4px rgba(255,255,255,0.3)`,
                },
              }}
            />
          )}

          {/* Sub Notaries Level */}
          {subNotaries.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  textAlign: "center",
                  mb: 2,
                  color: theme.palette.text.secondary,
                  fontWeight: "medium",
                }}
              >
                Sub Notaries
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: 2,
                }}
              >
                {subNotaries.map((notary, index) => (
                  <Zoom in={true} timeout={800 + index * 200} key={notary.id}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        cursor: "pointer",
                        transition: "transform 0.3s ease",
                        "&:hover": {
                          transform: "scale(1.05)",
                        },
                      }}
                      onClick={() => handleNotaryClick(notary)}
                    >
                      <Avatar
                        sx={{
                          width: nodeSize * 0.8,
                          height: nodeSize * 0.8,
                          border: isCurrentUser(notary)
                            ? `4px solid ${theme.palette.primary.main}`
                            : `3px solid ${getNotaryStatusColor(notary)}`,
                          boxShadow: `0 6px 24px rgba(0,0,0,0.1), 0 3px 12px rgba(0,0,0,0.06)`,
                          fontSize: isMobile ? "1.2rem" : "1.5rem",
                          fontWeight: "bold",
                          bgcolor: theme.palette.background.paper,
                          color: theme.palette.text.primary,
                          position: "relative",
                          overflow: "visible",
                          background: notary.avatar
                            ? undefined
                            : `linear-gradient(135deg, ${theme.palette.secondary.light}, ${theme.palette.primary.light})`,
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          "&:hover": {
                            transform: "translateY(-3px)",
                            boxShadow: `0 8px 32px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08)`,
                          },
                        }}
                        src={notary.avatar}
                      >
                        {(
                          notary.full_name?.charAt(0) ||
                          notary.first_name?.charAt(0) ||
                          "N"
                        ).toUpperCase()}
                        {isCurrentUser(notary) && (
                          <Chip
                            label="YOU"
                            size="small"
                            sx={{
                              position: "absolute",
                              bottom: -12,
                              left: "50%",
                              transform: "translateX(-50%)",
                              fontSize: "0.6rem",
                              height: 18,
                              bgcolor: theme.palette.primary.main,
                              color: "white",
                              fontWeight: "bold",
                              zIndex: 1,
                            }}
                          />
                        )}
                      </Avatar>
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 1,
                          textAlign: "center",
                          fontWeight: "medium",
                          maxWidth: nodeSize,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatNotaryName(notary)}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          mt: 0.5,
                          flexWrap: "wrap",
                          justifyContent: "center",
                        }}
                      >
                        {notary.on_site_registered && (
                          <Chip
                            label="On-Site"
                            size="small"
                            sx={{
                              fontSize: "0.6rem",
                              height: 16,
                              bgcolor: theme.palette.success.light,
                              color: theme.palette.success.contrastText,
                            }}
                          />
                        )}
                        {notary.bn_registered && (
                          <Chip
                            label="BN"
                            size="small"
                            sx={{
                              fontSize: "0.6rem",
                              height: 16,
                              bgcolor: theme.palette.info.main,
                              color: "white",
                              fontWeight: "bold",
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Zoom>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Fade>
    );
  };

  // Grid View Component
  const GridView = () => {
    const columns = [
      {
        field: "avatar",
        headerName: "",
        width: 60,
        renderCell: (params) => (
          <Avatar
            src={params.row.avatar}
            sx={{
              width: 40,
              height: 40,
              border: isCurrentUser(params.row)
                ? `2px solid ${theme.palette.primary.main}`
                : `1px solid ${getNotaryStatusColor(params.row)}`,
            }}
          >
            {(
              params.row.full_name?.charAt(0) ||
              params.row.first_name?.charAt(0) ||
              "N"
            ).toUpperCase()}
          </Avatar>
        ),
        sortable: false,
        filterable: false,
      },
      {
        field: "full_name",
        headerName: "Name",
        flex: 1,
        minWidth: 200,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: "medium" }}>
              {params.row.full_name ||
                `${params.row.first_name} ${params.row.last_name}`}
            </Typography>
            {isCurrentUser(params.row) && (
              <Chip
                label="YOU"
                size="small"
                sx={{
                  fontSize: "0.6rem",
                  height: 18,
                  bgcolor: theme.palette.primary.main,
                  color: "white",
                  fontWeight: "bold",
                }}
              />
            )}
          </Box>
        ),
      },
      {
        field: "email",
        headerName: "Email",
        flex: 2,
        minWidth: 200,
        renderCell: (params) => (
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary }}
          >
            {params.value}
          </Typography>
        ),
      },
      {
        field: "type",
        headerName: "Type",
        flex: 0.5,
        minWidth: 100,
        renderCell: (params) => {
          const isMain = mainNotaries.some((n) => n.id === params.row.id);
          return (
            <Chip
              label={isMain ? "Main" : "Sub"}
              size="small"
              sx={{
                bgcolor: isMain
                  ? theme.palette.primary.main
                  : theme.palette.secondary.main,
                color: "white",
                fontWeight: "bold",
              }}
            />
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.8,
        minWidth: 120,
        renderCell: (params) => (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {params.row.on_site_registered && (
              <Chip
                label="On-Site"
                size="small"
                sx={{
                  fontSize: "0.6rem",
                  height: 18,
                  bgcolor: theme.palette.success.main,
                  color: "white",
                  fontWeight: "bold",
                }}
              />
            )}
            {params.row.bn_registered && (
              <Chip
                label="BN"
                size="small"
                sx={{
                  fontSize: "0.6rem",
                  height: 18,
                  bgcolor: theme.palette.info.main,
                  color: "white",
                  fontWeight: "bold",
                }}
              />
            )}
            {params.row.is_deactivated && (
              <Chip
                label="Deactivated"
                size="small"
                sx={{
                  fontSize: "0.6rem",
                  height: 18,
                  bgcolor: theme.palette.error.light,
                  color: theme.palette.error.contrastText,
                }}
              />
            )}
          </Box>
        ),
      },
      {
        field: "jobs",
        headerName: "Jobs",
        flex: 0.6,
        minWidth: 100,
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: theme.palette.success.main }}
            >
              ✓ {params.row.completed_jobs || 0}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.warning.main }}
            >
              ⏳ {params.row.pending_jobs || 0}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: theme.palette.error.main }}
            >
              ✗ {params.row.cancelled_jobs || 0}
            </Typography>
          </Box>
        ),
      },
    ];

    return (
      <Fade in={true} timeout={800} key={`grid-${animationKey}`}>
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <DataGrid
              rows={allNotaries}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                  },
                },
              }}
              pageSizeOptions={[5, 10, 25]}
              disableRowSelectionOnClick
              onRowClick={(params) => handleNotaryClick(params.row)}
              sx={{
                minHeight: 400,
                "& .MuiDataGrid-row": {
                  cursor: "pointer",
                  py: 1,
                  borderBottom: `2px solid ${theme.palette.divider}`,
                  "&:hover": {
                    bgcolor: theme.palette.action.hover,
                  },
                  "&:last-child": {
                    borderBottom: "none",
                  },
                },
                "& .MuiDataGrid-cell": {
                  display: "flex",
                  alignItems: "center",
                  // justifyContent: "center",
                  "&:focus-within": {
                    outline: "none !important",
                  },
                },
                "& .MuiDataGrid-columnHeaders": {
                  "& .MuiDataGrid-columnHeader": {
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  },
                },
              }}
            />
          </CardContent>
        </Card>
      </Fade>
    );
  };

  // Notary Detail Modal
  const NotaryDetailModal = () => (
    <Modal
      open={modalOpen}
      onClose={handleCloseModal}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={modalOpen}>
        <Paper
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: isMobile ? "90%" : isTablet ? "80%" : "60%",
            maxWidth: 800,
            maxHeight: "90vh",
            overflow: "auto",
            p: 4,
            borderRadius: 2,
            boxShadow: theme.shadows[10],
          }}
        >
          {selectedNotary && (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 3,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Avatar
                    src={selectedNotary.avatar}
                    sx={{
                      width: 80,
                      height: 80,
                      border: isCurrentUser(selectedNotary)
                        ? `4px solid ${theme.palette.primary.main}`
                        : `3px solid ${getNotaryStatusColor(selectedNotary)}`,
                      fontSize: "2rem",
                      fontWeight: "bold",
                    }}
                  >
                    {selectedNotary.full_name?.charAt(0) ||
                      selectedNotary.first_name?.charAt(0) ||
                      "N"}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                      {selectedNotary.full_name ||
                        `${selectedNotary.first_name} ${selectedNotary.last_name}`}
                      {isCurrentUser(selectedNotary) && (
                        <Chip
                          label="YOU"
                          size="small"
                          sx={{
                            ml: 1,
                            fontSize: "0.7rem",
                            bgcolor: theme.palette.primary.main,
                            color: "white",
                            fontWeight: "bold",
                          }}
                        />
                      )}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                      {selectedNotary.on_site_registered && (
                        <Chip
                          label="On-Site Registered"
                          size="small"
                          icon={<CheckCircleIcon />}
                          sx={{
                            bgcolor: theme.palette.success.main,
                            color: theme.palette.success.contrastText,
                          }}
                        />
                      )}
                      {selectedNotary.bn_registered && (
                        <Chip
                          label="BN Registered"
                          size="small"
                          icon={<CheckCircleIcon />}
                          sx={{
                            bgcolor: theme.palette.info.main,
                            color: theme.palette.info.contrastText,
                          }}
                        />
                      )}
                      {selectedNotary.is_deactivated && (
                        <Chip
                          label="Deactivated"
                          size="small"
                          icon={<CancelIcon />}
                          sx={{
                            bgcolor: theme.palette.error.main,
                            color: theme.palette.error.contrastText,
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
                <IconButton
                  onClick={handleCloseModal}
                  sx={{ color: theme.palette.text.secondary }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                    Contact Information
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <EmailIcon sx={{ color: theme.palette.text.secondary }} />
                      <Typography variant="body2">
                        {selectedNotary.email}
                      </Typography>
                    </Box>
                    {selectedNotary.phone && selectedNotary.phone !== "N/A" && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <PhoneIcon
                          sx={{ color: theme.palette.text.secondary }}
                        />
                        <Typography variant="body2">
                          {selectedNotary.phone}
                        </Typography>
                      </Box>
                    )}
                    {selectedNotary.address &&
                      selectedNotary.address !== "N/A" && (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <LocationIcon
                            sx={{ color: theme.palette.text.secondary }}
                          />
                          <Typography variant="body2">
                            {selectedNotary.address}
                          </Typography>
                        </Box>
                      )}
                    {selectedNotary.state && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <LocationIcon
                          sx={{ color: theme.palette.text.secondary }}
                        />
                        <Typography variant="body2">
                          State: {selectedNotary.state}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                    Professional Details
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {selectedNotary.commission_number && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <BadgeIcon
                          sx={{ color: theme.palette.text.secondary }}
                        />
                        <Typography variant="body2">
                          Commission: {selectedNotary.commission_number}
                        </Typography>
                      </Box>
                    )}
                    {selectedNotary.commission_expiry && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <ScheduleIcon
                          sx={{ color: theme.palette.text.secondary }}
                        />
                        <Typography variant="body2">
                          Expires:{" "}
                          {new Date(
                            selectedNotary.commission_expiry * 1000
                          ).toLocaleDateString()}
                        </Typography>
                      </Box>
                    )}
                    {selectedNotary.created_at && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <ScheduleIcon
                          sx={{ color: theme.palette.text.secondary }}
                        />
                        <Typography variant="body2">
                          Joined: {selectedNotary.created_at}
                        </Typography>
                      </Box>
                    )}
                    {selectedNotary.total_worked_hours && (
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <ScheduleIcon
                          sx={{ color: theme.palette.text.secondary }}
                        />
                        <Typography variant="body2">
                          Total Hours: {selectedNotary.total_worked_hours}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                    Job Statistics
                  </Typography>
                  <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        variant="h4"
                        sx={{
                          color: theme.palette.success.main,
                          fontWeight: "bold",
                        }}
                      >
                        {selectedNotary.completed_jobs || 0}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        Completed
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        variant="h4"
                        sx={{
                          color: theme.palette.warning.main,
                          fontWeight: "bold",
                        }}
                      >
                        {selectedNotary.pending_jobs || 0}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        Pending
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        variant="h4"
                        sx={{
                          color: theme.palette.error.main,
                          fontWeight: "bold",
                        }}
                      >
                        {selectedNotary.cancelled_jobs || 0}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        Cancelled
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {selectedNotary.availabilities &&
                  selectedNotary.availabilities.length > 0 && (
                    <Grid size={{ xs: 12 }}>
                      <Typography
                        variant="h6"
                        sx={{ mb: 2, fontWeight: "bold" }}
                      >
                        Availability
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {formatAvailability(selectedNotary.availabilities)}
                      </Typography>
                    </Grid>
                  )}
              </Grid>
            </>
          )}
        </Paper>
      </Fade>
    </Modal>
  );

  if (!currentUser || !organizationData) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography
          variant="h6"
          sx={{ textAlign: "center", color: theme.palette.text.secondary }}
        >
          No organization data available
        </Typography>
      </Container>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        py: { xs: 1, sm: 2, md: 3 },
        backgroundColor: "#EEF2F6",
        minHeight: "100vh",
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
            flexDirection: isMobile ? "column" : "row",
            gap: 2,
          }}
        >
          <Typography
            variant={isMobile ? "h5" : "h4"}
            component="h1"
            color="primary"
            sx={{
              fontWeight: "bold",
            }}
          >
            My Organization
          </Typography>

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            aria-label="view mode"
            sx={{
              "& .MuiToggleButton-root": {
                px: 3,
                py: 1,
                border: `1px solid ${theme.palette.divider}`,
                "&.Mui-selected": {
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  "&:hover": {
                    bgcolor: theme.palette.primary.dark,
                  },
                },
              },
            }}
          >
            <ToggleButton value="hierarchy" aria-label="hierarchy view">
              <HierarchyIcon sx={{ mr: 1 }} />
              {!isMobile && "Hierarchy"}
            </ToggleButton>
            <ToggleButton value="grid" aria-label="grid view">
              <GridIcon sx={{ mr: 1 }} />
              {!isMobile && "Grid"}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Organization Stats */}
        <Card
          sx={{
            mb: 4,
            background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.grey[50]})`,
            boxShadow: `0 8px 32px rgba(0,0,0,0.08), 0 4px 16px rgba(0,0,0,0.04)`,
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <CardContent sx={{ py: 3 }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Zoom in={true} timeout={600}>
                  <Box
                    sx={{
                      textAlign: "center",
                      p: 2,
                      borderRadius: 2,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        bgcolor: theme.palette.primary.main + "08",
                      },
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: "bold",
                        color: theme.palette.primary.main,
                        textShadow: `0 2px 4px ${theme.palette.primary.main}20`,
                      }}
                    >
                      {allNotaries.length}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontWeight: "medium",
                      }}
                    >
                      Total Notaries
                    </Typography>
                  </Box>
                </Zoom>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Zoom in={true} timeout={800}>
                  <Box
                    sx={{
                      textAlign: "center",
                      p: 2,
                      borderRadius: 2,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        bgcolor: theme.palette.success.main + "08",
                      },
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: "bold",
                        color: theme.palette.success.main,
                        textShadow: `0 2px 4px ${theme.palette.success.main}20`,
                      }}
                    >
                      {mainNotaries.length}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontWeight: "medium",
                      }}
                    >
                      Main Notaries
                    </Typography>
                  </Box>
                </Zoom>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Zoom in={true} timeout={1000}>
                  <Box
                    sx={{
                      textAlign: "center",
                      p: 2,
                      borderRadius: 2,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        bgcolor: theme.palette.secondary.main + "08",
                      },
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: "bold",
                        color: theme.palette.secondary.main,
                        textShadow: `0 2px 4px ${theme.palette.secondary.main}20`,
                      }}
                    >
                      {subNotaries.length}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontWeight: "medium",
                      }}
                    >
                      Sub Notaries
                    </Typography>
                  </Box>
                </Zoom>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Zoom in={true} timeout={1200}>
                  <Box
                    sx={{
                      textAlign: "center",
                      p: 2,
                      borderRadius: 2,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        bgcolor: theme.palette.info.main + "08",
                      },
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: "bold",
                        color: theme.palette.info.main,
                        textShadow: `0 2px 4px ${theme.palette.info.main}20`,
                      }}
                    >
                      {
                        allNotaries.filter(
                          (n) => n.on_site_registered && n.bn_registered
                        ).length
                      }
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontWeight: "medium",
                      }}
                    >
                      Fully Registered
                    </Typography>
                  </Box>
                </Zoom>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Main Content */}
        {viewMode === "hierarchy" ? <HierarchicalView /> : <GridView />}

        {/* Detail Modal */}
        <NotaryDetailModal />
      </Container>
    </Box>
  );
};

export default NotaryOrganization;
